/**
 * Borehole-image geometry and painting, away from React.
 *
 * The server sends one depth window as two base64 byte buffers — the image and
 * a validity mask — and everything the screen draws on top of it is geometry
 * over the same two axes: depth down, azimuth across. Keeping that geometry
 * here rather than inside the canvas component is what lets the unrolled view,
 * the 3D cylinder and the tadpole track agree about where a plane sits, since
 * all three ask the same functions.
 *
 * The sinusoid maths is deliberately a *mirror* of `borehole/processing.py`
 * rather than an independent implementation: what is duplicated is only the
 * drawing of a plane whose dip the server already decided. Fitting stays on the
 * server — two least-squares solvers in two languages are two answers, and the
 * second one is wrong on the case nobody tested.
 */

import { rampColor, rampStops } from "./colorscale";

export interface ImageWindow {
  image_log: number;
  top: number;
  base: number;
  rows: number;
  bins: number;
  depth_step: number;
  stride: number;
  static_low: number;
  static_high: number;
  unit: string;
  /** Base64 uint8, `rows × bins`, row-major. Absent unless asked for. */
  static?: string;
  dynamic?: string;
  /** Base64 uint8: 255 where a pad touched the wall, 0 where nothing did. */
  mask: string;
}

/** Decoded window, ready to paint. */
export interface ImagePixels {
  rows: number;
  bins: number;
  data: Uint8Array;
  mask: Uint8Array;
}

export function decodeBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

export function pixelsFrom(win: ImageWindow, which: "static" | "dynamic"): ImagePixels | null {
  const payload = which === "dynamic" ? win.dynamic : win.static;
  if (!payload) return null;
  return {
    rows: win.rows,
    bins: win.bins,
    data: decodeBytes(payload),
    mask: decodeBytes(win.mask),
  };
}

/**
 * Paint a decoded window into an ImageData buffer.
 *
 * The gap behind a pad is painted transparent rather than white. White is a
 * colour the ramp itself produces at the resistive end, and a gap drawn in it
 * becomes indistinguishable from the most resistive rock in the well — which is
 * the one reading a geologist is most likely to act on.
 */
/**
 * Rentang bita yang direntang ke seluruh ramp — kontras citra.
 *
 * Citra FMI tiba sebagai bita 0–255 yang sudah dinormalkan di server, dan
 * sebuah jendela yang seluruh bacaannya jatuh di 90–160 tergambar dengan
 * sepertiga ramp saja: rekahan tipis dan laminasi yang dicari orang hilang di
 * antara dua warna yang nyaris sama. Menyetel ujungnya sendiri adalah cara
 * penafsir citra memperjelasnya. Kosong di salah satu sisi berarti 0 atau 255.
 */
export interface ByteStretch {
  min: number | null;
  max: number | null;
}

/** Kedudukan sebuah bita di ramp, sesudah direntang. */
export function stretchFraction(value: number, stretch?: ByteStretch): number {
  const lo = Math.max(0, Math.min(255, stretch?.min ?? 0));
  const hi = Math.max(0, Math.min(255, stretch?.max ?? 255));
  if (hi === lo) return value >= hi ? 1 : 0;
  return Math.min(1, Math.max(0, (value - lo) / (hi - lo)));
}

export function paint(
  pixels: ImagePixels,
  rampKey: string,
  reverse = false,
  stretch?: ByteStretch,
): ImageData {
  const stops = rampStops(rampKey);
  // 256 warna dihitung sekali, lalu dicari. Memanggil `rampColor` untuk tiap
  // piksel berarti jutaan interpolasi per jendela — dan hasilnya cuma 256
  // warna yang berbeda, sebab sumbernya memang satu bita.
  const lut = new Uint8Array(256 * 3);
  for (let i = 0; i < 256; i++) {
    const f = stretchFraction(i, stretch);
    const t = reverse ? 1 - f : f;
    const [r, g, b] = parseRgb(rampColor(stops, t));
    lut[i * 3] = r;
    lut[i * 3 + 1] = g;
    lut[i * 3 + 2] = b;
  }

  const out = new ImageData(pixels.bins, pixels.rows);
  const buffer = out.data;
  for (let i = 0; i < pixels.rows * pixels.bins; i++) {
    const value = pixels.data[i];
    const at = i * 4;
    if (!pixels.mask[i]) {
      buffer[at + 3] = 0;
      continue;
    }
    buffer[at] = lut[value * 3];
    buffer[at + 1] = lut[value * 3 + 1];
    buffer[at + 2] = lut[value * 3 + 2];
    buffer[at + 3] = 255;
  }
  return out;
}

function parseRgb(css: string): [number, number, number] {
  const match = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(css);
  if (!match) return [0, 0, 0];
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

/* ------------------------------------------------------------------ */
/* Plane geometry                                                      */
/* ------------------------------------------------------------------ */

export interface Feature {
  id: number;
  image_log: number;
  kind: string;
  kind_label: string;
  md: number;
  dip: number;
  azimuth: number;
  apparent_dip: number;
  apparent_azimuth: number;
  amplitude: number;
  aperture_mm: number | null;
  quality: string;
  picks: [number, number][];
  color: string;
  note: string;
}

export interface Breakout {
  id: number;
  image_log: number;
  kind: "breakout" | "ditf";
  kind_label: string;
  top_md: number;
  base_md: number;
  length: number;
  azimuth: number;
  width: number;
  quality: string;
  shmax_azimuth: number;
}

/** Half-height of the sinusoid a plane of this dip draws on the wall. */
export function amplitudeFor(dip: number, holeDiameter: number): number {
  const radius = Math.max(holeDiameter, 1e-6) / 2;
  return Math.tan((Math.min(Math.abs(dip), 89.9) * Math.PI) / 180) * radius;
}

/** Inverse — what dip a sinusoid of this height means. */
export function dipFor(amplitude: number, holeDiameter: number): number {
  const radius = Math.max(holeDiameter, 1e-6) / 2;
  return (Math.atan2(Math.abs(amplitude), radius) * 180) / Math.PI;
}

/**
 * Depth of a plane at a given azimuth — one point of its sinusoid.
 *
 * Drawn from the *apparent* dip, not the true one. What the wall shows is the
 * plane's attitude relative to the hole, and drawing the corrected dip would
 * put the curve somewhere the image does not show a boundary — a trace that
 * misses its own feature by a few centimetres and makes the reader distrust
 * both.
 */
export function depthAt(feature: Pick<Feature, "md" | "apparent_dip" | "apparent_azimuth">,
                        azimuth: number, holeDiameter: number): number {
  const amplitude = amplitudeFor(feature.apparent_dip, holeDiameter);
  return feature.md + amplitude * Math.cos(((azimuth - feature.apparent_azimuth) * Math.PI) / 180);
}

/** The whole sinusoid, sampled once per step of azimuth. */
export function sinusoidPath(feature: Pick<Feature, "md" | "apparent_dip" | "apparent_azimuth">,
                             holeDiameter: number, step = 4): [number, number][] {
  const points: [number, number][] = [];
  for (let azimuth = 0; azimuth <= 360; azimuth += step) {
    points.push([azimuth, depthAt(feature, azimuth, holeDiameter)]);
  }
  return points;
}

/**
 * Fit a sinusoid in the browser, for the ghost trace shown while picking.
 *
 * Deliberately *not* what gets stored. It is the same closed form the server
 * uses so the ghost does not jump when the answer comes back, but the number
 * saved on the feature is always the server's.
 */
export function previewFit(picks: [number, number][]):
    { md: number; amplitude: number; azimuth: number } | null {
  if (picks.length < 3) return null;
  // Normal equations for [1, cos θ, sin θ] — three unknowns, so the 3×3 solve
  // is written out rather than pulled from a matrix library.
  let n = 0, sc = 0, ss = 0, scc = 0, sss = 0, scs = 0;
  let sd = 0, sdc = 0, sds = 0;
  for (const [azimuth, depth] of picks) {
    const t = (azimuth * Math.PI) / 180;
    const c = Math.cos(t);
    const s = Math.sin(t);
    n += 1; sc += c; ss += s; scc += c * c; sss += s * s; scs += c * s;
    sd += depth; sdc += depth * c; sds += depth * s;
  }
  const a = [
    [n, sc, ss],
    [sc, scc, scs],
    [ss, scs, sss],
  ];
  const b = [sd, sdc, sds];
  const solution = solve3(a, b);
  if (!solution) return null;
  const [md, aCos, aSin] = solution;
  return {
    md,
    amplitude: Math.hypot(aCos, aSin),
    azimuth: ((Math.atan2(aSin, aCos) * 180) / Math.PI + 360) % 360,
  };
}

/** Gaussian elimination with partial pivoting; null when singular. */
function solve3(a: number[][], b: number[]): [number, number, number] | null {
  const m = a.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < 3; col++) {
    let pivot = col;
    for (let row = col + 1; row < 3; row++) {
      if (Math.abs(m[row][col]) > Math.abs(m[pivot][col])) pivot = row;
    }
    if (Math.abs(m[pivot][col]) < 1e-12) return null;
    [m[col], m[pivot]] = [m[pivot], m[col]];
    for (let row = 0; row < 3; row++) {
      if (row === col) continue;
      const factor = m[row][col] / m[col][col];
      for (let k = col; k < 4; k++) m[row][k] -= factor * m[col][k];
    }
  }
  return [m[0][3] / m[0][0], m[1][3] / m[1][1], m[2][3] / m[2][2]];
}

/* ------------------------------------------------------------------ */
/* Rose diagrams and the stress plot                                   */
/* ------------------------------------------------------------------ */

export interface Rose {
  counts: number[];
  bin_count: number;
  width: number;
  max: number;
  bidirectional: boolean;
}

/**
 * Wedge outlines for a rose, in SVG user units centred on (0, 0).
 *
 * Petal length goes as the *square root* of the count. A rose drawn with length
 * proportional to count exaggerates by area — the eye reads the wedge's area,
 * which then grows as the square — so a set with twice the picks looks four
 * times as dominant. Square-root scaling is what makes the area honest.
 */
export function rosePetals(rose: Rose, radius: number): string[] {
  const max = Math.max(rose.max, 1);
  const span = rose.bidirectional ? 180 : 360;
  const width = span / rose.bin_count;
  const petals: string[] = [];
  for (let i = 0; i < rose.counts.length; i++) {
    const count = rose.counts[i];
    if (!count) continue;
    const length = radius * Math.sqrt(count / max);
    const from = i * width;
    const to = from + width;
    petals.push(wedge(from, to, length));
    // Sisi seberangnya digambar juga, sebab sebuah bidang yang berjurus 030
    // dan yang berjurus 210 adalah bidang yang sama — dan mawar setengah
    // lingkaran terbaca seperti populasi yang hanya menghadap satu arah.
    if (rose.bidirectional) petals.push(wedge(from + 180, to + 180, length));
  }
  return petals;
}

function wedge(fromDeg: number, toDeg: number, length: number): string {
  const a = polar(fromDeg, length);
  const b = polar(toDeg, length);
  const large = toDeg - fromDeg > 180 ? 1 : 0;
  return `M 0 0 L ${a[0].toFixed(2)} ${a[1].toFixed(2)} `
    + `A ${length.toFixed(2)} ${length.toFixed(2)} 0 ${large} 1 `
    + `${b[0].toFixed(2)} ${b[1].toFixed(2)} Z`;
}

/** Compass degrees to SVG coordinates: north up, east right, y downwards. */
export function polar(azimuthDeg: number, radius: number): [number, number] {
  const t = ((azimuthDeg - 90) * Math.PI) / 180;
  return [radius * Math.cos(t), radius * Math.sin(t)];
}

/**
 * A bearing, rounded for reading, kept inside its own circle.
 *
 * Rounding is what invents "360°": a dip azimuth of 359.8° is eight tenths of a
 * degree short of north, and `toFixed(0)` hands the reader a bearing that does
 * not exist on any compass. So the wrap has to come *after* the rounding — it
 * is the rounding that pushes it over the top — which is why `% 360` on the
 * raw value does not help. Directions defined on a half circle, like SHmax and
 * everything else quoted modulo 180, pass their own period.
 */
export function bearing(deg: number, period = 360): string {
  const rounded = Math.round(deg);
  return String(((rounded % period) + period) % period);
}

/* ------------------------------------------------------------------ */
/* Tadpoles                                                            */
/* ------------------------------------------------------------------ */

/**
 * Where a tadpole sits in its track, and which way its tail points.
 *
 * The dot's x is the dip on a 0–90° scale; the tail points at the dip azimuth,
 * north being up the page. That convention is older than every piece of
 * software that draws it, so it is not configurable — a "tadpole" whose tail
 * means something else is a symbol that reads as a lie to anyone trained on it.
 */
export function tadpole(dip: number, azimuth: number, width: number, tail = 9) {
  const x = Math.min(Math.max(dip, 0), 90) / 90 * width;
  const t = ((azimuth - 90) * Math.PI) / 180;
  return { x, dx: Math.cos(t) * tail, dy: Math.sin(t) * tail };
}
